import { create } from 'zustand';

import type { LinkResolveDestination } from '@/core/lib/link-resolve-classify';
import { parseSetupTypeParam } from '@/core/lib/product-setup-type';
import type { LegacyProductCode } from '@up2stay/up2promo-api-client';

export type LinkResolvePhase = 'idle' | 'resolving' | 'resolved' | 'error';

export type LinkResolveRequest =
  | {
      source: 'hash';
      hash: string;
      productType?: LegacyProductCode | null;
    }
  | {
      source: 'profile';
      workspaceUsername: string;
      username: string;
      scanHash?: string | null;
    }
  | {
      /** Digital `/p/{publicHash}` — resolve via profiles-public, not product API. */
      source: 'profile-hash';
      hash: string;
      src?: string | null;
    };

type LinkResolveState = {
  open: boolean;
  phase: LinkResolvePhase;
  request: LinkResolveRequest | null;
  destination: LinkResolveDestination | null;
  errorMessage: string | null;
  openFromHash: (
    hash: string,
    productType?: LegacyProductCode | string | null,
  ) => void;
  openFromProfile: (
    workspaceUsername: string,
    username: string,
    scanHash?: string | null,
  ) => void;
  openFromProfileHash: (hash: string, src?: string | null) => void;
  setResolving: () => void;
  setResolved: (destination: LinkResolveDestination) => void;
  setError: (message: string) => void;
  close: () => void;
};

const idleState = {
  open: false,
  phase: 'idle' as const,
  request: null,
  destination: null,
  errorMessage: null,
};

export const useLinkResolveStore = create<LinkResolveState>((set) => ({
  ...idleState,
  openFromHash: (hash, productType) => {
    const trimmed = hash.trim();
    if (!trimmed) return;
    set({
      open: true,
      phase: 'resolving',
      request: {
        source: 'hash',
        hash: trimmed,
        productType:
          parseSetupTypeParam(
            productType == null ? undefined : String(productType),
          ) ?? null,
      },
      destination: null,
      errorMessage: null,
    });
  },
  openFromProfile: (workspaceUsername, username, scanHash) => {
    const ws = workspaceUsername.trim();
    const user = username.trim();
    if (!ws || !user) return;
    set({
      open: true,
      phase: 'resolving',
      request: {
        source: 'profile',
        workspaceUsername: ws,
        username: user,
        scanHash: scanHash?.trim() || null,
      },
      destination: null,
      errorMessage: null,
    });
  },
  openFromProfileHash: (hash, src) => {
    const trimmed = hash.trim();
    if (!trimmed) return;
    set({
      open: true,
      phase: 'resolving',
      request: {
        source: 'profile-hash',
        hash: trimmed,
        src: src?.trim() || null,
      },
      destination: null,
      errorMessage: null,
    });
  },
  setResolving: () =>
    set((state) =>
      state.open ? { ...state, phase: 'resolving', errorMessage: null } : state,
    ),
  setResolved: (destination) =>
    set((state) =>
      state.open
        ? {
            ...state,
            phase: 'resolved',
            destination,
            errorMessage: null,
          }
        : state,
    ),
  setError: (message) =>
    set((state) =>
      state.open
        ? {
            ...state,
            phase: 'error',
            destination: null,
            errorMessage: message,
          }
        : state,
    ),
  close: () => set({ ...idleState }),
}));

export function openLinkResolveFromHash(
  hash: string,
  productType?: LegacyProductCode | string | null,
): void {
  useLinkResolveStore.getState().openFromHash(hash, productType);
}

export function openLinkResolveFromProfile(
  workspaceUsername: string,
  username: string,
  scanHash?: string | null,
): void {
  useLinkResolveStore
    .getState()
    .openFromProfile(workspaceUsername, username, scanHash);
}

export function openLinkResolveFromProfileHash(
  hash: string,
  src?: string | null,
): void {
  useLinkResolveStore.getState().openFromProfileHash(hash, src);
}

export function closeLinkResolve(): void {
  useLinkResolveStore.getState().close();
}
